import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isNull } from 'lodash';
import {
  Popover,
} from '@unite-us/ui';
import {
  dates,
  TableRow,
  TableRowColumn,
  Popover as ToolTip,
} from '@unite-us/app-components';
import { tableWidth } from 'common/helpers/Tables';
import { viewable } from 'common/utils/Permission/Permission';
import { goToCase } from 'src/components/Cases/utils/routing';
import CaseRowIcon from 'src/components/Dashboard/components/Cases/CaseRowIcon';
import { useFeatureFlag } from 'common/hooks';
import './CaseTableRow.scss';

const handlePendingAuthPopover = ({
  isPendingAuth,
  pendingAuthPopoverOpen,
  togglePendingAuthPopover,
  closePendingAuthPopover,
}) => {
  if (isPendingAuth) {
    togglePendingAuthPopover();
    return true;
  }

  if (pendingAuthPopoverOpen) {
    closePendingAuthPopover();
  }

  return false;
};

const handleRowClick = ({
  isPendingAuth,
  pendingAuthPopoverOpen,
  togglePendingAuthPopover,
  closePendingAuthPopover,
  canView,
  onView,
  toggleUnauthorizedPopover,
}) => {
  const wasPendingAuthHandled = handlePendingAuthPopover({
    isPendingAuth,
    pendingAuthPopoverOpen,
    togglePendingAuthPopover,
    closePendingAuthPopover,
  });

  if (wasPendingAuthHandled) {
    return;
  }

  if (canView) {
    onView();
    return;
  }

  toggleUnauthorizedPopover();
};

const CaseTableRow = ({
  serviceCase,
  serviceType,
  serviceCaseGroup,
  index,
  groupId,
  isPendingAuth,
  parent,
  rowIndex,
  secondaryDataList,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pendingAuthPopoverOpen, setPendingAuthPopoverOpen] = useState(false);
  const togglePendingAuthPopover = () => setPendingAuthPopoverOpen(!pendingAuthPopoverOpen);
  const crtb2182ClarifyReferralLanguage = useFeatureFlag('crtb-2182-clarify-referral-language');

  const toggle = () => handleRowClick({
    isPendingAuth,
    pendingAuthPopoverOpen,
    togglePendingAuthPopover,
    closePendingAuthPopover: () => setPendingAuthPopoverOpen(false),
    canView: viewable(serviceCase),
    onView: () => {
      goToCase(serviceCase, {}, groupId);
    },
    toggleUnauthorizedPopover: () => setPopoverOpen((previous) => !previous),
  });

  // We don't have much room in the table and we don't want to overflow into
  // multiple lines, so we try to take only the first line. If the first line
  // is really long or the entire description is on one line that is alright
  // because we use CSS to truncate the description to ensure it only takes a
  // single line. This just makes it look nicer and allows users to have a
  // pseudo title for the case.

  const rowsWithSortingEnabled = (
    <>
      {crtb2182ClarifyReferralLanguage && (
        <TableRowColumn className="flex justify-center items-center" style={{ width: tableWidth('icon') }}>
          {serviceCase && serviceCase.state === 'off_platform' && (
          <div className="absolute">
            <ToolTip
              content={(
                <span className="text-white">
                  Off-Platform Case
                </span>
                )}
              trigger="hover"
              position="bottom-end"
              dark
            >
              <CaseRowIcon
                isCaseMgmtProvider
                serviceCase={serviceCase}
              />
            </ToolTip>
          </div>
        )}
        </TableRowColumn>
      )}
      <TableRowColumn style={{ width: tableWidth('medium') }}>
        {serviceType && serviceType.name}
      </TableRowColumn>
      <TableRowColumn style={{ width: tableWidth('small') }}>
        {isNull(serviceCase.opened_date) ?
          dates.formatDate(serviceCase.created_at) :
          dates.formatDate(serviceCase.opened_date)}
      </TableRowColumn>
      <TableRowColumn style={{ width: tableWidth('small') }}>
        {serviceCase.outcome ? 'Closed' : 'Open'}
      </TableRowColumn>
      <TableRowColumn style={{ width: tableWidth('large') }}>
        {serviceCaseGroup && serviceCaseGroup.name}
      </TableRowColumn>
      <TableRowColumn style={{ width: tableWidth('small') }}>
        {dates.formatDate(serviceCase.updated_at)}
      </TableRowColumn>

    </>
  );

  return (
    <TableRow
      className="case-row"
      key={`case-row-${index}`}
      id={`case-row-${serviceCase.id}`}
      onCellClick={toggle}
      clickable={!isPendingAuth}
      parent={parent}
      rowIndex={rowIndex}
      secondaryDataList={secondaryDataList}
    >
      {rowsWithSortingEnabled}

      <Popover
        placement="bottom"
        isOpen={popoverOpen}
        target={`case-row-${serviceCase.id}`}
        body="You do not have permission to access this case"
        toggle={toggle}
      />

      <Popover
        placement="bottom"
        isOpen={pendingAuthPopoverOpen}
        target={`case-row-${serviceCase.id}`}
        body="You can't access cases that are pending authorization. Try again later."
      />
    </TableRow>
  );
};

CaseTableRow.propTypes = {
  serviceCase: PropTypes.object.isRequired,
  serviceType: PropTypes.object.isRequired,
  serviceCaseGroup: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  groupId: PropTypes.string.isRequired,
  isPendingAuth: PropTypes.bool.isRequired,
  /** Inherited props from the TableBody Component * */
  parent: PropTypes.string,
  /** Inherited props from the TableBody Component * */
  rowIndex: PropTypes.number,
  /** Inherited props from the TableBody Component * */
  secondaryDataList: PropTypes.array,
};

export default CaseTableRow;
